(() => {
  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => [...context.querySelectorAll(selector)];

  const formatMoney = (cents, format = window.themeMoneyFormat || '${{amount}}') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents || 0);
    const withDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (Number.isNaN(number)) return '0';
      const parts = (number / 100).toFixed(precision).split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? parts.join(decimal) : parts[0];
    };
    const token = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!token) return withDelimiters(value);
    let output;
    switch (token[1]) {
      case 'amount_no_decimals': output = withDelimiters(value, 0); break;
      case 'amount_with_comma_separator': output = withDelimiters(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': output = withDelimiters(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': output = withDelimiters(value, 2, "'", '.'); break;
      default: output = withDelimiters(value, 2); break;
    }
    return format.replace(token[0], output);
  };

  const showToast = (message, includeCartLink = false) => {
    const toast = qs('[data-toast]');
    if (!toast) return;
    toast.textContent = message;
    if (includeCartLink) {
      const link = document.createElement('a');
      link.href = window.themeRoutes.cartUrl;
      link.textContent = 'View cart';
      toast.append(link);
    }
    toast.classList.add('is-visible');
    window.clearTimeout(showToast.timer);
    showToast.timer = window.setTimeout(() => toast.classList.remove('is-visible'), 4500);
  };

  const updateCartCount = async () => {
    try {
      const response = await fetch(`${window.themeRoutes.cartUrl}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      qsa('[data-cart-count]').forEach((element) => {
        element.textContent = cart.item_count;
        element.hidden = cart.item_count === 0;
      });
    } catch (_error) {
      // The cart page remains the reliable fallback.
    }
  };

  class ProductController {
    constructor(root) {
      this.root = root;
      const data = qs('[data-product-json]', root);
      if (!data) return;
      try {
        this.product = JSON.parse(data.textContent);
      } catch (_error) {
        return;
      }
      this.bind();
    }

    bind() {
      qsa('[data-option-input]', this.root).forEach((input) => {
        input.addEventListener('change', () => this.onVariantChange());
      });
      qsa('[data-quantity-change]', this.root).forEach((button) => {
        button.addEventListener('click', () => this.changeQuantity(Number(button.dataset.quantityChange)));
      });
      this.onVariantChange(false);
    }

    selectedOptions() {
      const groups = qsa('[data-option-group]', this.root);
      return groups.map((group) => {
        const selected = qs('[data-option-input]:checked', group) || qs('[data-option-input]', group);
        return selected ? selected.value : '';
      });
    }

    getVariant() {
      const options = this.selectedOptions();
      return this.product.variants.find((variant) => variant.options.every((option, index) => option === options[index]));
    }

    onVariantChange(updateUrl = true) {
      const variant = this.getVariant();
      qsa('input[name="id"]', this.root).forEach((input) => {
        input.value = variant ? variant.id : '';
      });

      const price = qs('[data-product-price]', this.root);
      const compare = qs('[data-product-compare-price]', this.root);
      const unitPrice = qs('[data-unit-price]', this.root);
      const submitButtons = qsa('[data-add-to-cart]', this.root);

      if (!variant) {
        submitButtons.forEach((button) => {
          button.disabled = true;
          qs('[data-add-to-cart-text]', button).textContent = window.themeStrings.unavailable;
        });
        return;
      }

      if (price) price.textContent = formatMoney(variant.price);
      if (compare) {
        compare.textContent = variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price) : '';
        compare.hidden = !(variant.compare_at_price > variant.price);
      }
      if (unitPrice) unitPrice.hidden = true;

      submitButtons.forEach((button) => {
        button.disabled = !variant.available;
        const text = qs('[data-add-to-cart-text]', button);
        if (text) text.textContent = variant.available ? window.themeStrings.addToCart : window.themeStrings.soldOut;
      });

      const sku = qs('[data-product-sku]', this.root);
      if (sku) sku.textContent = variant.sku || '';

      if (variant.featured_media) {
        const media = qs(`[data-media-id="${variant.featured_media.id}"]`, this.root);
        if (media) media.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }

      if (updateUrl && this.root.dataset.updateUrl === 'true' && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }

      this.root.dispatchEvent(new CustomEvent('variant:change', { detail: { variant }, bubbles: true }));
    }

    changeQuantity(change) {
      const input = qs('input[name="quantity"]', this.root);
      if (!input) return;
      const minimum = Number(input.min || 1);
      const maximum = input.max ? Number(input.max) : Infinity;
      input.value = Math.min(maximum, Math.max(minimum, Number(input.value || minimum) + change));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  const initProducts = (context = document) => {
    qsa('[data-product-root]:not([data-product-ready])', context).forEach((root) => {
      root.dataset.productReady = 'true';
      new ProductController(root);
    });
  };

  const initQuickView = () => {
    const dialog = qs('#QuickViewDialog');
    if (!dialog) return;
    const content = qs('[data-quick-view-content]', dialog);
    const close = qs('[data-quick-view-close]', dialog);

    document.addEventListener('click', async (event) => {
      const trigger = event.target.closest('[data-quick-view-url]');
      if (!trigger) return;
      event.preventDefault();
      trigger.setAttribute('aria-busy', 'true');
      content.innerHTML = '<div class="section"><p class="container">Loading…</p></div>';
      dialog.showModal();
      try {
        const separator = trigger.dataset.quickViewUrl.includes('?') ? '&' : '?';
        const response = await fetch(`${trigger.dataset.quickViewUrl}${separator}section_id=quick-view`);
        if (!response.ok) throw new Error('Quick View request failed');
        content.innerHTML = await response.text();
        initProducts(content);
        const title = qs('h2', content);
        if (title) title.id = 'QuickViewTitle';
      } catch (_error) {
        content.innerHTML = `<div class="section"><p class="container">${window.themeStrings.error}</p></div>`;
      } finally {
        trigger.removeAttribute('aria-busy');
      }
    });

    close.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      const bounds = dialog.getBoundingClientRect();
      const outside = event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom;
      if (outside) dialog.close();
    });
  };

  const initAjaxCart = () => {
    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('[data-ajax-cart]');
      if (!form) return;
      event.preventDefault();
      const button = qs('[type="submit"]', form);
      if (button) button.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${window.themeRoutes.cartAddUrl}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || window.themeStrings.error);
        await updateCartCount();
        showToast(window.themeStrings.added, true);
        const dialog = form.closest('dialog');
        if (dialog) dialog.close();
      } catch (error) {
        showToast(error.message || window.themeStrings.error);
      } finally {
        if (button) button.removeAttribute('aria-busy');
      }
    });
  };

  const initPredictiveSearch = () => {
    qsa('[data-predictive-search]').forEach((container) => {
      const input = qs('input[type="search"]', container);
      const results = qs('[data-predictive-results]', container);
      if (!input || !results) return;
      let request;
      input.addEventListener('input', () => {
        window.clearTimeout(request);
        const term = input.value.trim();
        if (term.length < 2) {
          results.hidden = true;
          results.innerHTML = '';
          return;
        }
        request = window.setTimeout(async () => {
          try {
            const url = `${window.themeRoutes.predictiveSearchUrl}?q=${encodeURIComponent(term)}&resources[type]=product,collection,page,article&resources[limit]=6&section_id=predictive-search`;
            const response = await fetch(url);
            if (!response.ok) return;
            results.innerHTML = await response.text();
            results.hidden = false;
          } catch (_error) {
            results.hidden = true;
          }
        }, 180);
      });
    });
  };

  const initDisclosures = () => {
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const openDialog = qs('dialog[open]');
      if (openDialog) openDialog.close();
      qsa('details[open]').forEach((details) => details.removeAttribute('open'));
    });

    qsa('[data-localization-select]').forEach((select) => {
      select.addEventListener('change', () => select.form.submit());
    });
  };

  const initStickyBuyBar = () => {
    const mainButton = qs('[data-main-add-to-cart]');
    const bar = qs('[data-sticky-buy-bar]');
    if (!mainButton || !bar || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(([entry]) => bar.classList.toggle('is-visible', !entry.isIntersecting), { threshold: 0 });
    observer.observe(mainButton);
    const stickyButton = qs('[data-sticky-add]', bar);
    if (stickyButton) stickyButton.addEventListener('click', () => mainButton.click());
    document.addEventListener('variant:change', (event) => {
      if (!event.detail.variant) return;
      const price = qs('[data-sticky-price]', bar);
      if (price) price.textContent = formatMoney(event.detail.variant.price);
      stickyButton.disabled = !event.detail.variant.available;
    });
  };

  const initThemeEditor = () => {
    document.addEventListener('shopify:section:load', (event) => {
      initProducts(event.target);
      initPredictiveSearch();
    });
  };

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const url = this.dataset.url;
      if (!url || this.dataset.loaded === 'true') return;
      this.dataset.loaded = 'true';
      fetch(url)
        .then((response) => response.text())
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const source = html.querySelector('product-recommendations');
          if (source && source.innerHTML.trim()) {
            this.innerHTML = source.innerHTML;
            initProducts(this);
          }
        })
        .catch(() => {});
    }
  }

  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', ProductRecommendations);
  }

  initProducts();
  initQuickView();
  initAjaxCart();
  initPredictiveSearch();
  initDisclosures();
  initStickyBuyBar();
  initThemeEditor();
})();
